import React, { useCallback, useEffect, useState } from 'react';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Card, CardContent } from '../ui/card';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Loader2, Bell } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';

const DELAY_PRESETS = [15, 30, 45, 60, 90];

const mapFromApi = (d) => ({
  is_enabled: !!d?.is_enabled,
  notification_delay_minutes: DELAY_PRESETS.includes(Number(d?.notification_delay_minutes))
    ? Number(d.notification_delay_minutes)
    : 30,
  notification_template: d?.notification_template || '',
  last_run_at: d?.last_run_at || null,
  last_run_summary: d?.last_run_summary || null,
  is_due_now: !!d?.is_due_now,
  migration_ready: d?.migration_ready !== false,
});

const AbsentNotificationSchedule = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const schoolId = user?.school_id;

  const [schedule, setSchedule] = useState(mapFromApi({}));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);

  const loadSchedule = useCallback(async () => {
    if (!schoolId) return;
    setLoading(true);
    try {
      const res = await api.get('/timetable/absent-notification-schedule', { params: { school_id: schoolId } });
      setSchedule(mapFromApi(res.data?.data || {}));
    } catch {
      // keep defaults
    } finally {
      setLoading(false);
    }
  }, [schoolId]);

  useEffect(() => {
    loadSchedule();
  }, [loadSchedule]);

  const handleSave = async () => {
    if (!schoolId) return;
    setSaving(true);
    try {
      const res = await api.put('/timetable/absent-notification-schedule', {
        school_id: schoolId,
        is_enabled: schedule.is_enabled,
        notification_delay_minutes: schedule.notification_delay_minutes,
        notification_template: schedule.notification_template || null,
      });
      setSchedule(mapFromApi(res.data?.data || {}));
      toast({ title: 'Absent notification schedule saved' });
    } catch (e) {
      toast({
        title: 'Save failed',
        description: e?.response?.data?.message || e?.message || 'Try again',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleRunNow = async () => {
    if (!schoolId) return;
    setRunning(true);
    try {
      const res = await api.post('/timetable/absent-notification-schedule/run-now', { school_id: schoolId });
      const run = res.data?.data?.run || {};
      setSchedule(mapFromApi(res.data?.data?.schedule || {}));
      toast({
        title: run.status === 'run' ? 'Notifications sent' : 'Run skipped',
        description: run.summary || run.reason || res.data?.message || 'Done',
        variant: run.status === 'run' ? 'default' : 'destructive',
      });
    } catch (e) {
      toast({
        title: 'Run failed',
        description: e?.response?.data?.message || e?.message || 'Try again',
        variant: 'destructive',
      });
    } finally {
      setRunning(false);
    }
  };

  const delayLabel = schedule.notification_delay_minutes;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Bell className="w-5 h-5" /> Absent notifications
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          When enabled, absent alerts are sent after a delay from the first attendance save for each class.
          Delivery runs automatically in the background — no cron required for local use.
          Parents and students receive in-app, push, email, and WhatsApp notifications.
        </p>
      </div>

      {!loading && schedule.is_enabled && !schedule.migration_ready && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm text-amber-800 dark:text-amber-200">
          Database migration required. Run <code className="font-mono text-xs">php spark migrate</code> in the backend folder to enable delayed absent notifications.
        </div>
      )}

      <Card className="border-border">
        <CardContent className="p-6 space-y-4">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : (
            <>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 rounded-lg border border-border p-4">
                <div>
                  <p className="font-medium">Enable scheduled absent notifications</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {schedule.is_enabled
                      ? `Absent alerts are sent ${delayLabel} minutes after attendance is first saved for a class.`
                      : 'Absent alerts (in-app, push, email, and WhatsApp) are sent immediately when attendance is marked.'}
                  </p>
                </div>
                <Switch
                  checked={!!schedule.is_enabled}
                  onCheckedChange={(v) => setSchedule((prev) => ({ ...prev, is_enabled: v }))}
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-2 max-w-xl">
                <div className="space-y-2">
                  <Label>Delay after first save</Label>
                  <Select
                    value={String(schedule.notification_delay_minutes)}
                    onValueChange={(v) => setSchedule((prev) => ({ ...prev, notification_delay_minutes: Number(v) }))}
                  >
                    <SelectTrigger className="bg-muted border-border">
                      <SelectValue placeholder="Select delay" />
                    </SelectTrigger>
                    <SelectContent className="bg-muted border-border">
                      {DELAY_PRESETS.map((minutes) => (
                        <SelectItem key={minutes} value={String(minutes)}>
                          {minutes} minutes
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Timer starts on the first save for each class/section/date. Re-saving attendance does not reset it.
                    Due notifications are sent automatically when users use the app or when cron runs every 5 minutes.
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Message template (optional)</Label>
                <Textarea
                  className="min-h-[80px] bg-muted border-border text-foreground"
                  value={schedule.notification_template}
                  onChange={(e) => setSchedule((prev) => ({ ...prev, notification_template: e.target.value }))}
                  placeholder="Absent: {student_name} on {date}"
                  capitalize={false}
                />
                <p className="text-xs text-muted-foreground">
                  Placeholders: {'{student_name}'}, {'{class_name}'}, {'{section_name}'}, {'{date}'}, {'{school_name}'}
                </p>
              </div>

              {schedule.last_run_at && (
                <div className="text-sm text-muted-foreground rounded-lg bg-muted/30 p-3">
                  <p>
                    <span className="font-medium text-foreground">Last run:</span>{' '}
                    {schedule.last_run_at}
                    {schedule.last_run_summary ? ` — ${schedule.last_run_summary}` : ''}
                  </p>
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                <Button type="button" className="bg-blue-600 hover:bg-blue-700" onClick={handleSave} disabled={saving}>
                  {saving ? 'Saving…' : 'Save schedule'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleRunNow}
                  disabled={running || !schedule.is_enabled}
                >
                  {running ? 'Running…' : 'Run now'}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AbsentNotificationSchedule;
